//#region IMPORTS
import {
  createInstoreOrderTypeAsync,
  updateInstoreOrderTypeAsync,
} from "../../features/instoreOrderTypes/instoreOrderTypesSlice";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { FieldValues, useForm } from "react-hook-form";
import { InstoreOrderType } from "../../models/instoreOrderType";
import { useEffect } from "react";
import { toast } from "react-toastify";
import SubmitButton from "../genericButtons/submitButton/SubmitButton";
import classNames from "../../helpers/classNames";
//#endregion

//#region INTERFACE
interface Props {
  instoreOrderType?: InstoreOrderType;
  cancelCUD: () => void;
  disabled?: boolean;
}
//#endregion

export default function InstoreOrderTypeForm({
  instoreOrderType,
  cancelCUD,
  disabled,
}: Props) {
  //#region SETUP
  // Redux
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector(
    ({ instoreOrderTypes }) => instoreOrderTypes
  );

  // UseForm
  const {
    reset,
    register,
    handleSubmit,
    formState: { isSubmitting, errors, isValid },
  } = useForm({ mode: "all" });
  //#endregion

  //#region LOGIC
  useEffect(() => {
    if (instoreOrderType) reset(instoreOrderType);
  }, [instoreOrderType, reset]);

  // Submit form
  async function submitForm(data: FieldValues) {
    try {
      if (instoreOrderType) {
        await dispatch(
          updateInstoreOrderTypeAsync({
            id: instoreOrderType.id,
            values: { name: data.name, short_name: data.short_name },
          })
        );
      } else {
        await dispatch(
          createInstoreOrderTypeAsync({
            values: { name: data.name, short_name: data.short_name },
          })
        );
      }
    } catch (error) {
      toast.error(`${error}`);
    }
    cancelCUD();
  }
  //#endregion

  return (
    <form onSubmit={handleSubmit(submitForm)}>
      <div className="space-y-12">
        <div className="pb-12">
          {/* HEADER */}
          <h2 className="text-base font-semibold leading-7 text-gray-900">
            {instoreOrderType
              ? `Update type: ${instoreOrderType.name}`
              : "Create new instore type"}
          </h2>

          {/* BODY */}
          <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            {/* name */}
            <fieldset className="sm:col-span-6">
              <label
                htmlFor="name"
                className={classNames(
                  errors.name ? "text-red-900" : "text-gray-900",
                  "block text-sm font-medium leading-6"
                )}
              >
                {errors.name ? errors?.name?.message : "order type name"}
              </label>
              <div className="relative mt-2">
                <input
                  placeholder="e.g. My first type"
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                  disabled={disabled || loading}
                  type="text"
                  {...register("name", {
                    required: "Name is required.",
                  })}
                />
              </div>
            </fieldset>

            {/* short_name */}
            <fieldset className="sm:col-span-6">
              <label
                htmlFor="short_name"
                className={classNames(
                  errors.short_name ? "text-red-900" : "text-gray-900",
                  "block text-sm font-medium leading-6"
                )}
              >
                {errors.short_name ? errors?.short_name?.message : "order type short name"}
              </label>
              <div className="relative mt-2">
                <input
                  placeholder="e.g. My first type"
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                  disabled={disabled || loading}
                  type="text"
                  {...register("short_name", {
                    required: "Short name is required.",
                  })}
                />
              </div>
            </fieldset>
          </div>
        </div>
      </div>

      {/* ACTIONS */}
      <div className="mt-6 flex items-center justify-end gap-x-6">
        <button
          onClick={cancelCUD}
          type="button"
          disabled={disabled || loading}
          className={classNames(
            disabled
              ? "text-gray-300 hover:cursor-not-allowed"
              : "text-rose-700",
            "text-sm font-semibold leading-6"
          )}
        >
          Cancel
        </button>

        <SubmitButton
          conditionals={[isValid]}
          isSubmitting={isSubmitting}
          disabled={disabled || loading}
        />
      </div>
    </form>
  );
}
